import { BaseOption as Option } from '@/context/SelectComponent/Base';
import { uniqueArray } from '@/utils/uniqueArray';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

type Model = {
  display_name?: string;
  name?: string;
  id?: string;
  code?: string | null;
  number?: string;
};
export type BaseOption = Option;
type options<T> = {
  labelKeys?: Array<keyof T>;
  value?: keyof T;
  caption?: keyof T | Array<keyof T>;
};

/**
 * @deprecated
 */
export const useModelsToOptions = <T extends Model>(
  query: string | undefined,
  defaultOptions: BaseOption[],
  option?: options<T>
) => {
  // NOTE: I don't think options change dynamically, so I'll make a note of it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { labelKeys, value, caption } = useMemo(() => option ?? {}, []);
  const memoLabelKeys: (keyof T)[] = useMemo(
    () => labelKeys ?? ['display_name', 'name'],
    [labelKeys]
  );
  const memoValue: keyof T = useMemo(() => value ?? 'id', [value]);
  const memoCaptions: (keyof T)[] | undefined = useMemo(
    () =>
      caption === undefined
        ? undefined
        : Array.isArray(caption)
        ? caption
        : [caption],
    [caption]
  );
  return useCallback(
    (items: T[]) => {
      const arr = items.map(
        (item): BaseOption => ({
          label:
            memoLabelKeys.map((v) => String(item[v])).filter(Boolean)?.[0] ??
            '',
          value: String(item[memoValue]),
          caption: memoCaptions
            ? memoCaptions
                .map((v) => String(item[v] ?? ''))
                .filter(Boolean)?.[0] ?? ''
            : undefined,
        })
      );
      return Array.from(
        new Map(
          [...(query ? [] : defaultOptions), ...arr].map((item) => [
            item.value,
            item,
          ])
        ).values()
      );
    },
    [query, defaultOptions, memoLabelKeys, memoValue, memoCaptions]
  );
};

export const useModelsToOptionsWithData = <T extends Model>(
  items: T[],
  query: string | undefined,
  defaultOptions: BaseOption[],
  option?: options<T>,
  showCode?: boolean
) => {
  // NOTE: I don't think options change dynamically, so I'll make a note of it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { labelKeys, value, caption } = useMemo(() => option ?? {}, []);
  const memoLabelKeys: (keyof T)[] = useMemo(
    () => labelKeys ?? ['display_name', 'name'],
    [labelKeys]
  );
  const memoValue: keyof T = useMemo(() => value ?? 'id', [value]);
  const memoCaptions: (keyof T)[] | undefined = useMemo(
    () =>
      caption === undefined
        ? undefined
        : Array.isArray(caption)
        ? caption
        : [caption],
    [caption]
  );
  return useMemo(() => {
    const arr = items.map(
      (item): BaseOption => ({
        label:
          memoLabelKeys
            .map((v) => String(item[v]))
            .filter(Boolean)?.[0]
            ?.concat(showCode ? `（${item.code}）` : '') ?? '',
        value: String(item[memoValue]),
        caption: memoCaptions
          ? memoCaptions
              .map((v) => String(item[v] ?? ''))
              .filter(Boolean)?.[0] ?? ''
          : undefined,
      })
    );
    return uniqueArray(query ? [] : defaultOptions, arr, 'value');
  }, [
    items,
    query,
    defaultOptions,
    memoLabelKeys,
    memoValue,
    memoCaptions,
    showCode,
  ]);
};

export const useModelsToOptionsWithCacheItems = <T extends Model>(
  items: T[],
  cacheKey: QueryKey | undefined,
  query: string | undefined,
  defaultOptions: BaseOption[],
  option?: options<T>
) => {
  // NOTE: I don't think options change dynamically, so I'll make a note of it.
  const queryClient = useQueryClient();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { labelKeys, value, caption } = useMemo(() => option ?? {}, []);
  const memoLabelKeys: (keyof T)[] = useMemo(
    () => labelKeys ?? ['display_name', 'name'],
    [labelKeys]
  );
  const memoValue: keyof T = useMemo(() => value ?? 'id', [value]);
  const memoCaptions: (keyof T)[] | undefined = useMemo(
    () =>
      caption === undefined
        ? undefined
        : Array.isArray(caption)
        ? caption
        : [caption],
    [caption]
  );
  return useMemo(() => {
    const cacheItems = cacheKey
      ? queryClient.getQueryData<T[]>(cacheKey) || []
      : [];
    const arr = (query ? items : uniqueArray(items, cacheItems, 'id')).map(
      (item): BaseOption => ({
        label:
          memoLabelKeys.map((v) => String(item[v])).filter(Boolean)?.[0] ?? '',
        value: String(item[memoValue]),
        caption: memoCaptions
          ? memoCaptions
              .map((v) => String(item[v] ?? ''))
              .filter(Boolean)?.[0] ?? ''
          : undefined,
      })
    );
    return uniqueArray(query ? [] : defaultOptions, arr, 'value');
  }, [
    queryClient,
    cacheKey,
    query,
    items,
    defaultOptions,
    memoLabelKeys,
    memoValue,
    memoCaptions,
  ]);
};

export const modelsToOptionsWithData = <T extends Model>(
  items: T[],
  query: string | undefined,
  defaultOptions: BaseOption[],
  option?: options<T>
) => {
  const { labelKeys, value, caption } = option ?? {};
  const memoLabelKeys: (keyof T)[] = labelKeys ?? ['display_name', 'name'];

  const memoValue: keyof T = value ?? 'id';
  const memoCaptions: (keyof T)[] | undefined =
    caption === undefined
      ? undefined
      : Array.isArray(caption)
      ? caption
      : [caption];
  const arr = items.map(
    (item): BaseOption => ({
      label:
        memoLabelKeys.map((v) => String(item[v])).filter(Boolean)?.[0] ?? '',
      value: String(item[memoValue]),
      caption: memoCaptions
        ? memoCaptions.map((v) => String(item[v] ?? '')).filter(Boolean)?.[0] ??
          ''
        : undefined,
    })
  );
  return uniqueArray(query ? [] : defaultOptions, arr, 'value');
};
